import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { BASE_URL } from 'config/api';

// ─── Helpers ────────────────────────────────────────────────
const formatTime = (timestamp) => {
  if (!timestamp) return 'Il y a quelques instants';
  const now  = new Date();
  const time = new Date(timestamp);
  const mins = Math.floor((now - time) / 60000);
  if (mins < 1)  return 'Il y a quelques instants';
  if (mins < 60) return `Il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `Il y a ${hrs}h`;
  return `Il y a ${Math.floor(hrs / 24)}j`;
};

// Dédoublonne par `id` (les éléments sans id sont gardés)
const dedupe = (list) => {
  const seen = new Set();
  return list.filter((n) => {
    if (!n.id) return true;
    if (seen.has(n.id)) return false;
    seen.add(n.id);
    return true;
  });
};

// ─── Ligne d'alerte réclamation ──────────────────────────────
const AlerteRow = ({ notification, onClose, navigate, textColor, textColorSecondary, borderColor, hoverBg }) => (
  <Box
    px="24px"
    py="16px"
    borderBottom="1px solid"
    borderColor={borderColor}
    cursor="pointer"
    transition="all 0.2s"
    _hover={{ bg: hoverBg }}
    bg={notification.statut === 'nonlue' ? 'orange.50' : 'transparent'}
    onClick={() => {
      onClose();
      if (notification.reclamation_id) {
        navigate(`/admin/reclamations?open=${notification.reclamation_id}&t=${Date.now()}`);
      } else {
        navigate('/admin/reclamations');
      }
    }}
  >
    <Flex justifyContent="space-between" alignItems="flex-start">
      <Box flex="1">
        <Text fontSize="sm" fontWeight="700" color={textColor} mb="4px">
          {notification.message}
        </Text>
        <Text fontSize="xs" color={textColorSecondary}>
          {formatTime(notification.created_at || notification.date_notification)}
          {notification.reclamation_id && (
            <Text as="span" ml="6px" color="orange.500" fontWeight="600">
              · Voir la réclamation →
            </Text>
          )}
        </Text>
      </Box>
      {notification.statut === 'nonlue' && (
        <Box w="8px" h="8px" bg="orange.400" borderRadius="50%" mt="4px" flexShrink={0} />
      )}
    </Flex>
  </Box>
);

// ─── Composant principal ─────────────────────────────────────
const NotificationsModal = ({
  isOpen,
  onClose,
  unreadMessages,
  missions,
  ambulanciers,
  totalUnreadMessages,
  markMessagesAsRead,
  newNotifications = [],
  userId,
}) => {
  const navigate = useNavigate();

  // ── Alertes (DB + temps réel, dédoublonnées) ──
  const [dbAlertes,      setDbAlertes]      = React.useState([]);
  const [alertesLoading, setAlertesLoading] = React.useState(false);

  // Synchronise les notifications temps réel depuis le parent (pas d'accumulation locale)
  const alertes = React.useMemo(
    () => dedupe([...newNotifications.filter((n) => n.type === 'alerte'), ...dbAlertes]),
    [newNotifications, dbAlertes]
  );

  // Charger les alertes depuis la DB à l'ouverture
  React.useEffect(() => {
    if (!isOpen || !userId) return;
    setAlertesLoading(true);
    fetch(`${BASE_URL}/notifications/me/alertes`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setDbAlertes(Array.isArray(data) ? data : []))
      .catch(() => setDbAlertes([]))
      .finally(() => setAlertesLoading(false));
  }, [isOpen, userId]);

  // Marquer les alertes comme lues à la fermeture
  const handleClose = React.useCallback(() => {
    if (userId && alertes.some((n) => n.statut === 'nonlue')) {
      fetch(`${BASE_URL}/notifications/me/alertes/mark-read`, {
        method: 'PUT',
        credentials: 'include',
      }).catch(() => {});
      // Mise à jour optimiste locale
      setDbAlertes((prev) => prev.map((n) => ({ ...n, statut: 'lue' })));
    }
    onClose();
  }, [onClose, userId, alertes]);

  // Chakra tokens
  const menuBg              = useColorModeValue('white', 'navy.800');
  const textColor           = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary  = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const textColorBrand      = useColorModeValue('brand.500', 'brand.400');
  const borderColor         = useColorModeValue('gray.200', 'whiteAlpha.200');
  const tabColor            = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const tabActiveColor      = useColorModeValue('brand.500', 'brand.400');
  const hoverBg             = useColorModeValue('gray.50', 'whiteAlpha.50');
  const shadow              = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );

  // Compteurs pour les badges
  const unreadAlertes   = alertes.filter((n) => n.statut === 'nonlue').length;
  const missionMessages = Object.entries(unreadMessages || {});

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <ModalOverlay />
      <ModalContent
        position="fixed"
        top="0"
        right="0"
        bottom="0"
        left="50vw"
        width="50vw"
        maxW="50vw"
        height="100vh"
        boxShadow={shadow}
        p="0"
        borderRadius="20px"
        bg={menuBg}
        m="2"
        border="none"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px="24px"
          py="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Text fontSize="xl" fontWeight="700" color={textColor}>
            Notifications
          </Text>
          <ModalCloseButton position="relative" top="0" right="0" />
        </Flex>

        <ModalBody p="0" flex="1" overflowY="auto">
          <Tabs variant="unstyled">
            {/* Tab Headers */}
            <TabList px="24px" pt="16px" borderBottom="1px solid" borderColor={borderColor}>
              {/* Tab Messages */}
              <Tab
                fontSize="sm"
                fontWeight="600"
                color={tabColor}
                pb="12px"
                px="16px"
                _selected={{ color: tabActiveColor, borderBottom: '2px solid', borderColor: tabActiveColor }}
                _hover={{ color: tabActiveColor }}
              >
                Messages
                {totalUnreadMessages > 0 && (
                  <Badge ml="2" colorScheme="blue" borderRadius="full" fontSize="10px">
                    {totalUnreadMessages}
                  </Badge>
                )}
              </Tab>

              {/* Tab Alertes */}
              <Tab
                fontSize="sm"
                fontWeight="600"
                color={tabColor}
                pb="12px"
                px="16px"
                _selected={{ color: tabActiveColor, borderBottom: '2px solid', borderColor: tabActiveColor }}
                _hover={{ color: tabActiveColor }}
              >
                Alertes
                {unreadAlertes > 0 && (
                  <Badge ml="2" colorScheme="orange" borderRadius="full" fontSize="10px">
                    {unreadAlertes}
                  </Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              {/* ── Panel Messages (missions) ── */}
              <TabPanel p="0">
                <Flex flexDirection="column">
                  {missionMessages.length === 0 ? (
                    <Box px="24px" py="40px" textAlign="center">
                      <Text fontSize="sm" color={textColorSecondary}>Aucun message non lu</Text>
                    </Box>
                  ) : (
                    missionMessages.map(([missionId, count]) => {
                      const mission      = missions?.find((m) => m.id === parseInt(missionId));
                      const ambulancier  = mission?.ambulancier_id ? ambulanciers?.[mission.ambulancier_id] : null;
                      const ambulancierName = ambulancier
                        ? `${ambulancier.nom} ${ambulancier.prenom}`.trim()
                        : 'Ambulancier inconnu';
                      return (
                        <Box
                          key={missionId}
                          px="24px"
                          py="16px"
                          borderBottom="1px solid"
                          borderColor={borderColor}
                          cursor="pointer"
                          transition="all 0.2s"
                          _hover={{ bg: hoverBg }}
                          onClick={async () => {
                            try { await markMessagesAsRead(parseInt(missionId)); } catch (_) {}
                            onClose();
                            navigate(`/admin/missions?openChat=${missionId}`);
                          }}
                        >
                          <Flex justifyContent="space-between" alignItems="flex-start">
                            <Box flex="1">
                              <Text fontSize="sm" fontWeight="700" color={textColor} mb="4px">
                                Nouveau message — mission #{missionId}
                              </Text>
                              <Text fontSize="sm" color={textColorSecondary} mb="4px">
                                {count} message{count > 1 ? 's' : ''} de {ambulancierName}
                              </Text>
                              <Text fontSize="xs" color={textColorBrand} fontWeight="500">
                                Voir la mission →
                              </Text>
                            </Box>
                            <Badge colorScheme="blue" borderRadius="full">{count}</Badge>
                          </Flex>
                        </Box>
                      );
                    })
                  )}
                </Flex>
              </TabPanel>

              {/* ── Panel Alertes (réclamations) ── */}
              <TabPanel p="0">
                {alertesLoading ? (
                  <Flex justify="center" py="40px">
                    <Spinner size="md" color="orange.400" />
                  </Flex>
                ) : alertes.length === 0 ? (
                  <Box px="24px" py="40px" textAlign="center">
                    <Text fontSize="sm" color={textColorSecondary}>Aucune alerte</Text>
                  </Box>
                ) : (
                  <Flex flexDirection="column">
                    {alertes.map((notification, idx) => (
                      <AlerteRow
                        key={notification.id ?? `rt-${idx}`}
                        notification={notification}
                        onClose={handleClose}
                        navigate={navigate}
                        textColor={textColor}
                        textColorSecondary={textColorSecondary}
                        borderColor={borderColor}
                        hoverBg={hoverBg}
                      />
                    ))}
                  </Flex>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NotificationsModal;

